import React, {
  useMemo,
  Fragment,
  useState,
  useContext,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import injectSaga from 'redux-utils/injectSaga';
import injectReducer from 'redux-utils/injectReducer';
import saga from 'containers/User/saga';
import reducer from 'containers/User/reducer';
import {
  OnboardingInput,
  ONBOARDING_EMPTY_ERROR_KEY,
  useValidEmail,
} from 'components/onboarding/components/OnboardingInput';
import { TelmexButton } from 'components/onboarding/components/TelmexButton';
import { OnboardingLayout } from 'layouts/OnboardingLayout';
import { OnboardingButton } from 'components/onboarding/components/DefaultButton';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import { useVerifyIsMobile } from 'hooks/useVerifyIsMobile';
import { actions as getUserInfoActions } from 'action-creators/user/getUserInfo';
import { UserContext } from 'providers/user.provider';
import { actionUserLogInEmail } from 'containers/User/actions';
import { getLoginState } from 'containers/User/selectors/loginEmail';
import { setMetricsEvent } from 'action-creators/metrics';
import { GetRegion } from 'constants.js';
import { compose } from 'redux';
import { getRegion } from 'utils/Region';
import './style.scss';
import useMenuRedirect from 'containers/Menu/hooks/useMenuRedirect';
import getQueryParameter from 'utils/nav/getQueryParameter';
import { apiCall, updateSwaggerManager } from 'api';
import {
  setIsEmailRegister,
  setUserInfo,
} from 'containers/UserSuspendedModal/redux/actions/setUserInfo.action';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import { triggerBanner } from 'reducers/videoBanner/videoBanner.actions';
import { OnboardingLoading } from 'components/onboarding/components/OnboardingLoading';
import { useMetrics } from 'hooks/metrics/useMetrics';
import TelmexIframeModal from 'components/TelmexIframeModal';
import useExternalTelmexLogin from 'components/TelmexIframeModal/utils/useExternalLoginTelmex';

const TRANSLATION_KEYS = {
  title: 'Onboarding2_IniciaSesion_Texto_Titulo',
  section1Title: 'Onboarding2_IniciaSesion_Opcion1_Texto_Titulo',
  section2Title: 'Onboarding2_IniciaSesion_Opcion2_Texto_Titulo',
  providerText: 'Onboarding2_IniciaSesion_Opcion1_Texto_Parrafo',
  loginText: 'Onboarding2_IniciaSesion_Opcion2_Texto_Parrafo',
  emailLabel: 'Onboarding2_Input_Texto_CorreoElectronico',
  passwordLabel: 'Onboarding2_Input_Texto_Contrasena',
  forgottenPassText:
    'Onboarding2_IniciaSesion_Opcion2_Enlace_Texto_OlvidasteContrasena',
  continueButton: 'Onboarding2_Boton_Texto_Continuar',
  emailInvalid: 'Onboarding2_Input_TextoError_CorreoInvalido',
  passwordInvalid: 'Onboarding2_Input_TextoError_ContrasenaInvalida',
  requiredInput: 'Onboarding2_Input_TextoError_CampoObligatorio',
  correoNoVinculado: 'Onboarding2_Input_TextoError_CorreoNoVinculado',
};

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const translations = useSelector(state => state.translations);
  const { buildMetricsEvent, sendMetricsEvent } = useMetrics();
  const { login } = useContext(UserContext);
  const { isMobile } = useVerifyIsMobile(768);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { loading, error } = useSelector(getLoginState);
  const [showLoader, setShowLoader] = useState(false);
  const { firstNode } = useMenuRedirect();
  const telmexIframeConf = useSelector(
    selectMetadataKeyByRegion('iframe_telmex_config'),
  );
  const {
    isOpen,
    setIsOpen,
    setRequestError,
    showLoaderRequest,
    requestError,
    handleExternalLogin,
  } = useExternalTelmexLogin();

  useEffect(
    () => {
      if (params.get('fromForgot')) {
        const userInfo = JSON.parse(
          localStorage.getItem('getUserInfo') || '{}',
        );

        setEmail(userInfo.email);
      }
    },
    [localStorage.getItem('getUserInfo')],
  );

  useValidEmail(email, setEmailError, emailError);

  const translationMapper = (key, maxLength = null) => {
    const text = getTranslationEmptyOrNullKey(
      TRANSLATION_KEYS[key],
      translations,
    );
    return maxLength ? renderMaxLengthText(text, maxLength) : text;
  };

  useEffect(
    () => {
      if (error && error?.code) {
        setShowLoader(false);
        setPasswordError(texts.passwordInvalid);
        dispatch(
          setMetricsEvent({
            tagManager: {
              event: 'login_error',
              eventLabel: 'password_invalid',
            },
          }),
        );
      }
    },
    [error],
  );

  const texts = useMemo(
    () => ({
      title: translationMapper('title', 28),
      section1Title: translationMapper('section1Title', 35),
      section2Title: translationMapper('section2Title', 35),
      providerText: translationMapper('providerText', 38),
      loginText: translationMapper('loginText', 38),
      emailLabel: translationMapper('emailLabel', 38),
      passwordLabel: translationMapper('passwordLabel', 38),
      forgottenPassText: translationMapper('forgottenPassText', 46),
      continueButton: translationMapper('continueButton', 22),
      emailInvalid: translationMapper('emailInvalid', 46) || ONBOARDING_EMPTY_ERROR_KEY,
      passwordInvalid: translationMapper('passwordInvalid', 46) || ONBOARDING_EMPTY_ERROR_KEY,
      requiredInput: translationMapper('requiredInput', 46) || ONBOARDING_EMPTY_ERROR_KEY,
      correoNoVinculado: translationMapper('correoNoVinculado') || ONBOARDING_EMPTY_ERROR_KEY,
    }),
    [translations],
  );

  const validateEmail = async () => {
    try {
      if (!email) throw error;
      const unregisteredUser = await dispatch(
        getUserInfoActions.getUserInfo({ email }),
      );

      const getUserInfo = JSON.parse(
        localStorage.getItem('getUserInfo') || '{}',
      );
      const regionFromStartHeaderInfo = JSON.parse(
        localStorage.getItem('startHeaderInfoResponse') || '{}',
      );
      const isEmailRegister = !unregisteredUser;
      dispatch(setUserInfo(getUserInfo));
      dispatch(setIsEmailRegister(isEmailRegister));
      const apiParams = {
        user_id: getUserInfo?.id_usuario,
        user_token: getUserInfo?.token,
        region: regionFromStartHeaderInfo?.response?.region || getRegion(),
      };
      if (getUserInfo && getUserInfo?.region_nombre) {
        apiParams.region = getUserInfo?.region_nombre;
      }

      updateSwaggerManager(apiCall, apiParams);
      localStorage.setItem('isEmailRegister', isEmailRegister);

      if (unregisteredUser === true) {
        setEmailError(texts.correoNoVinculado);
        return false;
      }
      setEmailError('');
      return true;
    } catch {
      setEmailError(texts.requiredInput);
      return false;
    }
  };

  const handleLogin = async () => {
    let success = false;
    try {
      await new Promise((resolve, reject) => {
        dispatch(
          actionUserLogInEmail({
            email,
            password,
            login,
            claroLiteConfig: false,
            callback: response => {
              if (response.error || response.code) {
                reject(response?.error || response?.code);
                success = false;
              } else {
                const eventSuccess = buildMetricsEvent('login', {
                  user_id: response?.user_id,
                  parent_id: response?.parent_id,
                  user_id_custom: response?.user_id,
                  user_type: 'registrado',
                  content_section: 'inicio',
                  method: 'correo electronico',
                });

                sendMetricsEvent(eventSuccess);
                resolve(response.data);
                success = true;
              }
            },
          }),
        );
      });

      return success;
    } catch (error) {
      return false;
    }
  };

  const handleContinue = async () => {
    setShowLoader(true);
    setEmailError('');
    setPasswordError('');

    const event = buildMetricsEvent('interaction_login', {
      content_section: 'inicio',
      interaction_type: texts.continueButton?.toLowerCase() || 'continuar',
      modulo_name: 'inicio',
      method: 'correo electronico',
    });

    sendMetricsEvent(event);

    const emailOk = await validateEmail();

    if (!emailOk) {
      setShowLoader(false);
      return;
    }

    if (!password) {
      setPasswordError(texts.requiredInput);
      setShowLoader(false);
      return;
    }

    const success = await handleLogin();

    if (success) {
      if (params.get('anonymousUserComeFromPlayer')) {
        localStorage.setItem('anonymousUserComeFromPlayer', true);
      }
      if (params.get('urlVcard')) {
        localStorage.setItem('urlVcard', true);
      }
      if (getQueryParameter('addFavorite') === 'true') {
        localStorage.setItem('addToFavorite', true);
      }
      localStorage.removeItem('defaultLiveChannel');
      dispatch(triggerBanner('login'));

      const hasPaymentFlow =
        params.get('linkworkflowstart') ||
        params.get('group_id') ||
        params.get('prepago');
      if (hasPaymentFlow) return;

      const existingParams = new URLSearchParams(location?.search || '');
      existingParams.set('login', 'true');


      navigate(`/${GetRegion()}/${firstNode}?${existingParams.toString()}`, {
        ...location.state,
        replace: true,
      });
    }
  };

  return (
    <>
      {isOpen && (
        <TelmexIframeModal
          onSuccess={handleExternalLogin}
          isOpen={isOpen}
          requestError={requestError}
          onClose={() => {
            setRequestError(false);
            setIsOpen(false);
          }}
          moduleName="login"
        />
      )}
      <OnboardingLoading show={showLoader || showLoaderRequest} />
      <OnboardingLayout type="login">
        <Fragment>
          <h2 className="LoginWrapper__Title">{texts.title}</h2>
          <div className="LoginWrapper">
            <div className="LoginWrapper__Section1">
              <h3 className="LoginWrapper__Section1__Title">
                {texts.section1Title}
              </h3>
              <span
                className="LoginWrapper__Section1__Label"
                style={{ marginBottom: '24px' }}
              >
                {telmexIframeConf?.enable && texts.providerText}
              </span>
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                {telmexIframeConf?.enable && (
                  <TelmexButton
                    className="LoginWrapper__Section1__TelmexButton"
                    onClick={() => {
                      const event = buildMetricsEvent('interaction_login', {
                        content_section: 'inicio',
                        interaction_type: 'telmex',
                        modulo_name: 'tambien puedes registrarte con tu proveedor',
                        method: 'mi telmex',
                      });

                      sendMetricsEvent(event);
                      setIsOpen(true);
                    }}
                  />
                )}
              </div>
            </div>
            <div className="LoginWrapper__Separator" />
            <div className="LoginWrapper__Section2">
              <div style={{ height: '28px', marginBottom: '24px' }}>
                <h3 className="LoginWrapper__Section2__Title">
                  {texts.section2Title}
                </h3>
              </div>
              <div style={{ height: '24px', marginBottom: '16px' }}>
                <span
                  className="LoginWrapper__Section2__Label"
                  style={{ marginBottom: '16px' }}
                >
                  {texts.loginText}
                </span>
              </div>

              <OnboardingInput
                type="text"
                name="loginEmail"
                value={email}
                onChange={e => {
                  setEmailError('');
                  setEmail(e.target.value);
                }}
                label={texts.emailLabel}
                showError={!!emailError}
                error={emailError}
                autoComplete="off"
                styles={{ width: isMobile ? '100%' : 320, maxWidth:320, marginBottom: 16 }}
              />

              <OnboardingInput
                type="password"
                name="loginPassword"
                value={password}
                onChange={e => {
                  setPasswordError('');
                  setPassword(e.target.value);
                }}
                label={texts.passwordLabel}
                showError={!!passwordError}
                autoComplete="off"
                error={passwordError}
                styles={{ width: isMobile ? '100%' : 320, maxWidth: 320, marginBottom: 24 }}
              />
              <div className="LoginWrapper__Section2__ContainerLink">
                <button
                  type="button"
                  className="LoginWrapper__Section2__Link"
                  onClick={() => {
                    const event = buildMetricsEvent(
                      'interaction_forgotten_password',
                      {
                        content_section: 'inicia sesion',
                        interaction_type:
                          texts.forgottenPassText.toLowerCase() ||
                          'obtener contraseña',
                        modulo_name: 'inicia sesion',
                      },
                    );

                    sendMetricsEvent(event);

                    navigate(`/${getRegion()}/recoveryPassword`, {
                      replace: true,
                    });
                  }}
                >
                  {texts.forgottenPassText}
                </button>
              </div>
              <OnboardingButton
                variant="secondary"
                size="large"
                textContent={texts.continueButton}
                className="LoginWrapper__Section2__Button"
                onClick={handleContinue}
                disabled={loading || false}
              />
            </div>
          </div>
        </Fragment>
      </OnboardingLayout>
    </>
  );
};
const withSaga = injectSaga({ key: 'sso', saga });
const withReducer = injectReducer({ key: 'sso', reducer });

export default compose(
  withSaga,
  withReducer,
)(LoginPage);
